import { useRef, useState } from 'react'
import { AlertTriangle, Pencil } from 'lucide-react'
import { cn } from 'components/UI/cn'
import { Tooltip } from 'components/Tooltip/tooltip'
import { RowIssue } from '../contactValidation'

interface EditableCellProps {
  value: string
  /** All issues for this row; the cell filters to its own `field`. */
  issues: RowIssue[]
  field: string
  onChange: (v: string) => void
}

/**
 * Inline editable grid cell that toggles between a read view and a text input.
 *
 * Uses a draft-value pattern: typing updates only local `draft` state.
 * `onChange` (which dispatches to wizard state) is called only on commit
 * (blur or Enter). Escape discards the draft and exits without committing.
 *
 * Commit semantics — exactly one `onChange` call per accepted edit:
 * - Enter  → `e.currentTarget.blur()` triggers `onBlur={commit}` (one commit).
 *            No direct `commit()` call in the keydown handler to avoid double-fire.
 * - Escape → sets `cancelledRef` then blurs; `onBlur` detects the flag and
 *            skips the commit, so zero commits happen.
 * - Plain blur → `onBlur={commit}` fires once as normal.
 *
 * Issue styling (matches the design mockup):
 * - Error / duplicate cells: red wavy underline + a ⚠ AlertTriangle icon.
 * - Warning cells:           amber wavy underline + a ⚠ AlertTriangle icon.
 * - On hover, flagged cells surface their issue message(s) via the shared
 *   `Tooltip` (the mockup uses click; we use hover with our standard tooltip).
 * - On hover, an issue-free editable cell shows a faint Pencil edit affordance.
 * - Empty cells show "empty" (italic) when flagged, "—" otherwise.
 */
export function EditableCell({
  value,
  issues,
  field,
  onChange,
}: EditableCellProps) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState('')
  // Tracks whether the current blur was triggered by Escape; if so, skip commit.
  const cancelledRef = useRef(false)

  const fieldIssues = issues.filter((i) => i.field === field)
  // Precedence when a cell carries multiple issues: error > duplicate > warning.
  const severity = fieldIssues.some((i) => i.severity === 'error')
    ? 'error'
    : fieldIssues.some((i) => i.severity === 'duplicate')
      ? 'duplicate'
      : fieldIssues.some((i) => i.severity === 'warning')
        ? 'warning'
        : null
  // Errors and duplicates both render red; warnings render amber.
  const tone = severity === 'warning' ? 'amber' : severity ? 'red' : null
  // Tooltip body: one line per issue, using the tooltip's default text color.
  const tooltipContent = severity ? (
    <div className="flex flex-col gap-1">
      {fieldIssues.map((iss, idx) => (
        <span key={`${iss.field}-${idx}`} className="text-xs">
          {iss.message}
        </span>
      ))}
    </div>
  ) : undefined

  /** Activate edit mode, initialising draft to the current committed value. */
  function activate() {
    cancelledRef.current = false
    setDraft(value)
    setEditing(true)
  }

  /**
   * Commit the draft to wizard state and exit edit mode.
   * Called only from onBlur — never directly from the Enter keydown handler —
   * so Enter → blur → commit fires the callback exactly once.
   */
  function commit() {
    if (cancelledRef.current) {
      // Blur was caused by Escape; do not propagate the draft value.
      cancelledRef.current = false
      setEditing(false)
      return
    }
    onChange(draft)
    setEditing(false)
  }

  /** Discard the draft and exit edit mode without touching wizard state. */
  function cancel(e: React.KeyboardEvent<HTMLInputElement>) {
    e.preventDefault()
    cancelledRef.current = true
    e.currentTarget.blur()
  }

  if (editing) {
    return (
      <input
        autoFocus
        type="text"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            // Blur the input; the resulting onBlur will call commit() once.
            e.currentTarget.blur()
          } else if (e.key === 'Escape') {
            cancel(e)
          }
        }}
        className={cn(
          'w-full min-w-0 rounded px-1.5 py-0.5 text-xs outline-none border',
          tone === 'red'
            ? 'border-error-border bg-error-bg text-error focus:ring-1 focus:ring-error-border'
            : tone === 'amber'
              ? 'border-warning-border bg-warning-bg text-warning focus:ring-1 focus:ring-warning-border'
              : 'border-brand bg-surface text-fg focus:ring-1 focus:ring-brand'
        )}
      />
    )
  }

  const isEmpty = value === ''

  const cell = (
    <button
      type="button"
      onClick={activate}
      className="group/cell flex w-full min-w-0 items-center gap-1 rounded px-1.5 py-0.5 text-left text-xs transition-colors hover:bg-surface-hover"
    >
      {severity && (
        <AlertTriangle
          className={cn(
            'size-3 shrink-0',
            tone === 'red' ? 'text-error' : 'text-warning'
          )}
        />
      )}
      <span
        className={cn(
          'min-w-0 flex-1 truncate',
          isEmpty
            ? 'italic text-fg-muted'
            : tone === 'red'
              ? 'text-error underline decoration-wavy decoration-error underline-offset-2'
              : tone === 'amber'
                ? 'text-warning underline decoration-wavy decoration-warning underline-offset-2'
                : 'text-fg'
        )}
      >
        {isEmpty ? (severity ? 'empty' : '—') : value}
      </span>
      {!severity && (
        <Pencil className="size-3 shrink-0 text-fg-muted opacity-0 transition-opacity group-hover/cell:opacity-100" />
      )}
    </button>
  )

  // No issue → render the bare cell (Tooltip would no-op anyway).
  if (!tooltipContent) return cell

  // Flagged → reveal the issue message on hover via the shared Tooltip.
  // `wrapTrigger={false}` keeps the table cell layout intact (no extra div).
  return (
    <Tooltip
      content={tooltipContent}
      placement="top"
      compact
      wrapTrigger={false}
    >
      {cell}
    </Tooltip>
  )
}
